import { apiClient } from '../api/apiClient';

export interface LogLevel {
  ERROR: 'error';
  WARN: 'warn';
  INFO: 'info';
  DEBUG: 'debug';
}

export const LOG_LEVELS: LogLevel = {
  ERROR: 'error',
  WARN: 'warn',
  INFO: 'info',
  DEBUG: 'debug'
};

export interface FrontendErrorLog {
  message: string;
  stack?: string;
  url?: string;
  userAgent?: string;
  componentStack?: string;
  props?: any;
  state?: any;
  timestamp?: Date;
  level?: string;
  correlationId?: string;
}

export interface FrontendLog {
  message: string;
  level: string;
  data?: any;
  url?: string;
  userAgent?: string;
  timestamp?: Date;
  correlationId?: string;
}

export interface UserActionLog {
  action: string;
  data?: any;
  url?: string;
  userAgent?: string;
  timestamp?: Date;
  duration?: number;
  success?: boolean;
  correlationId?: string;
}

class LoggingService {
  private correlationId: string | null = null;
  private logQueue: Array<() => Promise<void>> = [];
  private isProcessingQueue = false;
  private maxRetries = 3;
  private retryDelay = 1000; // 1 second

  constructor() {
    this.generateCorrelationId();
    this.setupGlobalErrorHandlers();
  }

  private generateCorrelationId(): string {
    this.correlationId = `frontend-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    return this.correlationId;
  }

  public getCorrelationId(): string {
    return this.correlationId || this.generateCorrelationId();
  }

  public setCorrelationId(id: string): void {
    this.correlationId = id;
  }

  private setupGlobalErrorHandlers(): void {
    // Only set up global error handlers in web environment
    if (typeof window !== 'undefined' && window.addEventListener) {
      // Handle unhandled promise rejections
      window.addEventListener('unhandledrejection', (event) => {
        this.logError('Unhandled Promise Rejection', {
          reason: event.reason,
          promise: event.promise?.toString(),
          stack: event.reason?.stack
        });
      });

      // Handle global errors
      window.addEventListener('error', (event) => {
        this.logError('Global Error', {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error?.toString(),
          stack: event.error?.stack
        });
      });
    }
  }

  private getCurrentUrl(): string {
    if (typeof window !== 'undefined' && window.location) {
      return window.location.href;
    }
    return 'https://mobile.app/guard-dashboard'; // React Native fallback with proper URL format
  }

  private getUserAgent(): string {
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      return navigator.userAgent;
    }
    return 'React Native App'; // React Native fallback
  }

  private async queueLog(logFunction: () => Promise<void>): Promise<void> {
    this.logQueue.push(logFunction);
    if (!this.isProcessingQueue) {
      this.processQueue();
    }
  }

  private async processQueue(): Promise<void> {
    this.isProcessingQueue = true;
    
    while (this.logQueue.length > 0) {
      const logFunction = this.logQueue.shift();
      if (logFunction) {
        try {
          await logFunction();
        } catch (error) {
          console.error('Failed to send log to backend:', error);
          // Don't retry here to avoid infinite loops
        }
      }
    }
    
    this.isProcessingQueue = false;
  }

  private async sendLogWithRetry(
    endpoint: string, 
    payload: any, 
    retries = this.maxRetries
  ): Promise<void> {
    try {
      console.log(`📤 Sending log to: ${endpoint}`, { 
        correlationId: payload.correlationId,
        level: payload.level || payload.action || 'unknown',
        payloadPreview: {
          action: payload.action,
          duration: payload.duration,
          durationType: typeof payload.duration
        }
      });
      
      const response = await apiClient.post(endpoint, payload);
      
      if (response.success) {
        console.log(`✅ Log sent successfully to: ${endpoint}`, {
          correlationId: (response.data as any)?.correlationId || payload.correlationId
        });
      } else {
        throw new Error(`API response indicated failure: ${response.message}`);
      }
    } catch (error) {
      const errorDetails = {
        endpoint,
        correlationId: payload.correlationId,
        retries: this.maxRetries - retries,
        error: error instanceof Error ? error.message : 'Unknown error',
        status: (error as any)?.response?.status,
        statusText: (error as any)?.response?.statusText,
        payloadDebug: {
          action: payload.action,
          duration: payload.duration,
          durationType: typeof payload.duration,
          hasRequiredFields: {
            action: !!payload.action,
            timestamp: !!payload.timestamp,
            correlationId: !!payload.correlationId
          }
        }
      };
      
      console.error(`❌ Failed to send log to: ${endpoint}`, errorDetails);
      
      if (retries > 0) {
        console.log(`🔄 Retrying log send (${this.maxRetries - retries + 1}/${this.maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, this.retryDelay));
        return this.sendLogWithRetry(endpoint, payload, retries - 1);
      }
      
      // Final fallback - log to console with full details
      console.error('🚨 Failed to send log after all retries:', {
        ...errorDetails,
        payload: {
          message: payload.message || payload.action,
          level: payload.level,
          timestamp: payload.timestamp,
          fullPayload: payload
        }
      });
      
      // Don't throw error to prevent breaking the application
      // Just log the failure for debugging
    }
  }

  public async logError(
    message: string, 
    errorData?: any, 
    componentStack?: string,
    props?: any,
    state?: any
  ): Promise<void> {
    const errorLog: FrontendErrorLog = {
      message,
      stack: errorData?.stack || (errorData instanceof Error ? errorData.stack : undefined),
      url: this.getCurrentUrl(),
      userAgent: this.getUserAgent(),
      componentStack,
      props,
      state,
      timestamp: new Date(),
      level: LOG_LEVELS.ERROR,
      correlationId: this.getCorrelationId()
    };

    // Always log to console for immediate visibility
    console.error('Frontend Error:', message, errorData);

    await this.queueLog(async () => {
      await this.sendLogWithRetry('/Logging/frontend-error', errorLog);
    });
  }

  public async logWarning(message: string, data?: any): Promise<void> {
    const log: FrontendLog = {
      message,
      level: LOG_LEVELS.WARN,
      data,
      url: this.getCurrentUrl(),
      userAgent: this.getUserAgent(),
      timestamp: new Date(),
      correlationId: this.getCorrelationId()
    };

    console.warn('Frontend Warning:', message, data);

    await this.queueLog(async () => {
      await this.sendLogWithRetry('/Logging/frontend-log', log);
    });
  }

  public async logInfo(message: string, data?: any): Promise<void> {
    const log: FrontendLog = {
      message,
      level: LOG_LEVELS.INFO,
      data,
      url: this.getCurrentUrl(),
      userAgent: this.getUserAgent(),
      timestamp: new Date(),
      correlationId: this.getCorrelationId()
    };

    console.info('Frontend Info:', message, data);

    await this.queueLog(async () => {
      await this.sendLogWithRetry('/Logging/frontend-log', log);
    });
  }

  public async logDebug(message: string, data?: any): Promise<void> {
    // Only log debug messages in development
    if (process.env.NODE_ENV === 'development') {
      const log: FrontendLog = {
        message,
        level: LOG_LEVELS.DEBUG,
        data,
        url: this.getCurrentUrl(),
        userAgent: this.getUserAgent(),
        timestamp: new Date(),
        correlationId: this.getCorrelationId()
      };

      console.debug('Frontend Debug:', message, data);

      await this.queueLog(async () => {
      await this.sendLogWithRetry('/Logging/frontend-log', log);
      });
    }
  }

  public async logUserAction(
    action: string, 
    data?: any, 
    duration?: number,
    success: boolean = true
  ): Promise<void> {
    // Ensure duration is a positive number if provided, using Math.floor for consistent integer conversion
    const processedDuration = duration !== undefined ? Math.max(0, Math.floor(duration)) : undefined;
    
    const userActionLog: UserActionLog = {
      action,
      data,
      url: this.getCurrentUrl(),
      userAgent: this.getUserAgent(),
      timestamp: new Date(),
      duration: processedDuration,
      success,
      correlationId: this.getCorrelationId()
    };

    console.log('User Action:', action, data, { duration: processedDuration });

    await this.queueLog(async () => {
      await this.sendLogWithRetry('/Logging/user-action', userActionLog);
    });
  }

  // Performance logging
  public startPerformanceTimer(action: string): () => void {
    const startTime = performance.now();
    
    return () => {
      const duration = performance.now() - startTime;
      // Convert duration to integer milliseconds for backend compatibility using Math.floor
      const durationMs = Math.floor(duration);
      this.logUserAction(action, { performanceMetric: true }, durationMs);
    };
  }

  // API call logging
  public async logApiCall(
    method: string,
    url: string,
    statusCode: number,
    duration: number,
    success: boolean,
    errorData?: any
  ): Promise<void> {
    const message = `API Call: ${method} ${url}`;
    const data = {
      method,
      url,
      statusCode,
      duration,
      success,
      errorData
    };

    if (success) {
      await this.logInfo(message, data);
    } else {
      await this.logError(message, data);
    }
  }

  // Page navigation logging
  public async logPageNavigation(from: string, to: string): Promise<void> {
    await this.logUserAction('page_navigation', {
      from,
      to,
      timestamp: new Date()
    });
  }

  // Feature usage logging
  public async logFeatureUsage(feature: string, data?: any): Promise<void> {
    await this.logUserAction('feature_usage', {
      feature,
      ...data
    });
  }
}

// Create singleton instance
export const loggingService = new LoggingService();

// Export convenience functions
export const logError = (message: string, error?: any, componentStack?: string, props?: any, state?: any) => 
  loggingService.logError(message, error, componentStack, props, state);

export const logWarning = (message: string, data?: any) => 
  loggingService.logWarning(message, data);

export const logInfo = (message: string, data?: any) => 
  loggingService.logInfo(message, data);

export const logDebug = (message: string, data?: any) => 
  loggingService.logDebug(message, data);

export const logUserAction = (action: string, data?: any, duration?: number, success?: boolean) => 
  loggingService.logUserAction(action, data, duration, success);

export const startPerformanceTimer = (action: string) => 
  loggingService.startPerformanceTimer(action);

export const logApiCall = (method: string, url: string, statusCode: number, duration: number, success: boolean, errorData?: any) => 
  loggingService.logApiCall(method, url, statusCode, duration, success, errorData);

export const logPageNavigation = (from: string, to: string) => 
  loggingService.logPageNavigation(from, to);

export const logFeatureUsage = (feature: string, data?: any) => 
  loggingService.logFeatureUsage(feature, data);

export default loggingService;
